import { useState, useCallback } from "react"
import { useCore } from "./useCore"
import { useApp } from "./useApp"
import { useShopify } from "./useShopify"
import { useAnalytics } from "../analytics/provider"
import { useToasts } from "react-toast-notifications"
import { useCheckoutContext, useCheckout } from "./useCheckout"
import { useAnalytics as useTracking } from "./useAnalytics"
const config = require("./../../config.default")
declare global {
  interface Window {
    addToCartKlaviyo: any
    addToCartTikTok: any
    ttq: any
  }
}
export const useCart = () => {
  const { addToast } = useToasts()
  const {
    helpers: { storage, decodeShopifyId },
    graphql: {
      mutations: { CHECKOUT_LINE_ITEMS_REPLACE },
    },
  } = useCore()
  const {
    config: {
      settings: { keys },
    },
  } = useApp()
  const { checkout } = useCheckoutContext()
  const { setCheckout } = useCheckout()
  const { useMutation, checkoutNormaliser } = useShopify()
  const { trackCartUpdate } = useTracking()
  const [loading, setLoading] = useState(false)
  const [errors, setErrors] = useState([])
  const checkoutId = checkout?.id || storage.get(keys?.checkout)

  const [lineItemsReplace] = useMutation(CHECKOUT_LINE_ITEMS_REPLACE)

  const analytics = useAnalytics()

  const addToCart = useCallback(
    async (variantId, quantity = 1, customAttributes = []) => {
      setLoading(true)
      let alreadyInCart = false

      const lineItems =
        checkout?.lineItems?.map(lineItem => {
          if (lineItem?.variant?.id === variantId) {
            alreadyInCart = true
            return {
              customAttributes: [
                ...lineItem?.customAttributes?.map(({ key, value }) => ({
                  key,
                  value,
                })),
                ...(customAttributes || []),
              ],
              quantity: lineItem?.quantity + quantity,
              variantId,
            }
          }
          return {
            customAttributes: lineItem?.customAttributes?.map(({ key, value }) => ({
              key,
              value,
            })),
            quantity: lineItem?.quantity,
            variantId: lineItem?.variant?.id,
          }
        }) || []

      const {
        data: { checkoutLineItemsReplace: data, userErrors: errors },
      } = await lineItemsReplace({
        variables: {
          checkoutId,
          lineItems: [...(alreadyInCart ? lineItems : [...lineItems, { quantity, variantId, customAttributes }])],
        },
      })

      if (errors?.length) setErrors(errors)
      if (data) await setCheckout(data?.checkout)
      setLoading(false)

      const normalisedCheckout = checkoutNormaliser(data?.checkout)

      if (normalisedCheckout) {
        trackCartUpdate("add", variantId, quantity, normalisedCheckout.lineItems)

        const targetLineItem = normalisedCheckout.lineItems?.find(lineItem => lineItem.variant?.id === variantId)

        if (targetLineItem?.variant?.product) {
          const cp = targetLineItem.variant.compareAtPriceV2 != null ? targetLineItem.variant.compareAtPriceV2.amount : 0

          const klaviyoPayload = {
            Name: targetLineItem.title,
            ProductID: decodeShopifyId(targetLineItem.variant.product.id, "Product"),
            Categories: targetLineItem.variant.product.tags?.find(tag => tag.startsWith("category:"))?.replace("category:", "") || "Product",
            ImageURL: targetLineItem.variant.image.originalSrc,
            URL: config.app.url + "/" + targetLineItem.variant.product.handle,
            Brand: targetLineItem.variant.product.vendor,
            Price: targetLineItem.variant.priceV2.amount * 100,
            CompareAtPrice: cp,
            Quantity: quantity,
            Type: targetLineItem?.variant?.product?.productType,
            Currency: targetLineItem.variant.priceV2.currencyCode,
          }
          window.addToCartKlaviyo(klaviyoPayload)
          analytics.addToCart({
            product: {
              id: decodeShopifyId(targetLineItem.variant.product.id, "Product"),
              title: targetLineItem.title,
              category: targetLineItem.variant.product.tags?.find(tag => tag.startsWith("category:"))?.replace("category:", "") || "Product",
              image: targetLineItem.variant.image.originalSrc,
              brand: targetLineItem.vendor,
              link: config.app.url + targetLineItem.link,
              comparePrice: cp,
              sku: targetLineItem.variant.sku,
              price: targetLineItem.variant.priceV2.amount * 100,
              currency: targetLineItem.variant.priceV2.currencyCode,
            },
            quantity,
          })
        }
      }
    },

    [lineItemsReplace, setErrors, setCheckout, setLoading, trackCartUpdate, checkout, checkoutId]
  )

  const multiAddToCart = useCallback(
    async (variantIds = [], quantity = 1, customAttributes = []) => {
      setLoading(true)
      const hash = variantIds.reduce((result, id) => {
        result[id] = "new"
        return result
      }, {})

      const lineItems =
        checkout?.lineItems?.map(lineItem => {
          if (hash[lineItem?.variant?.id]) {
            hash[lineItem?.variant?.id] = "already in cart"
            return {
              customAttributes: [
                ...lineItem?.customAttributes?.map(({ key, value }) => ({
                  key,
                  value,
                })),
                ...(customAttributes || []),
              ],
              quantity: lineItem?.quantity + quantity,
              variantId: lineItem?.variant?.id,
            }
          }
          return {
            customAttributes: lineItem?.customAttributes?.map(({ key, value }) => ({
              key,
              value,
            })),
            quantity: lineItem?.quantity,
            variantId: lineItem?.variant?.id,
          }
        }) || []

      const newLineItems = variantIds.reduce((result, variantId) => {
        if (hash[variantId] !== "already in cart") {
          result.push({ quantity, variantId, customAttributes })
        }
        return result
      }, [])

      const {
        data: { checkoutLineItemsReplace: data, userErrors: errors },
      } = await lineItemsReplace({
        variables: {
          checkoutId,
          lineItems: [...lineItems, ...newLineItems],
        },
      })

      if (errors?.length) setErrors(errors)
      if (data) await setCheckout(data?.checkout)
      setLoading(false)

      for (const variantId of variantIds) {
        trackCartUpdate("add", variantId, quantity, checkoutNormaliser(data?.checkout)?.lineItems)
      }
    },
    [lineItemsReplace, setErrors, setCheckout, setLoading, trackCartUpdate, checkout, checkoutId, checkoutNormaliser]
  )

  const removeFromCart = useCallback(
    async variantId => {
      setLoading(true)
      const quantity = checkout?.lineItems.filter(({ variant }) => variant.id === variantId).map(({ quantity }) => quantity)[0] || 1
      trackCartUpdate("remove", variantId, quantity, checkout?.lineItems)
      addToast("Removing item...", { autoDismiss: true, autoDismissTimeout: 1000, transitionState: "none" })
      const lineItems = checkout?.lineItems
        .filter(lineItem => lineItem.variant.id !== variantId)
        .map(lineItem => ({
          ...(lineItem.customAttributes && {
            customAttributes: lineItem.customAttributes.map(({ key, value }) => ({ key, value })),
          }),
          quantity: lineItem.quantity,
          variantId: lineItem.variant.id,
        }))
      const {
        data: { checkoutLineItemsReplace: data, userErrors: errors },
      } = await lineItemsReplace({
        variables: {
          checkoutId,
          lineItems,
        },
      })

      if (errors?.length) setErrors(errors)
      if (data) await setCheckout(data?.checkout)
      if (data)
        await setTimeout(() => {
          addToast("Item removed!", { autoDismiss: true, autoDismissTimeout: 1500, transitionState: "none" })
        }, 500)
      setLoading(false)
    },
    [lineItemsReplace, setErrors, setCheckout, setLoading, trackCartUpdate, checkout, checkoutId]
  )

  const updateQuantity = useCallback(
    async (variantId, quantity, action = "add") => {
      setLoading(true)
      const lineItems = checkout?.lineItems.map(lineItem => ({
        ...(lineItem.customAttributes && {
          customAttributes: lineItem.customAttributes.map(({ key, value }) => ({ key, value })),
        }),
        quantity: lineItem.variant.id === variantId ? quantity : lineItem.quantity,
        variantId: lineItem.variant.id,
      }))

      const {
        data: { checkoutLineItemsReplace: data, userErrors: errors },
      } = await lineItemsReplace({
        variables: {
          checkoutId,
          lineItems,
        },
      })

      if (errors?.length) setErrors(errors)
      if (data) await setCheckout(data?.checkout)
      setLoading(false)

      trackCartUpdate(action, variantId, quantity, checkoutNormaliser(data?.checkout)?.lineItems)
    },
    [lineItemsReplace, setErrors, setCheckout, setLoading, trackCartUpdate, checkout, checkoutId, checkoutNormaliser]
  )

  const updateVariant = useCallback(
    async (prevVariantId, variantId) => {
      setLoading(true)
      const lineItems = checkout?.lineItems.map(lineItem => ({
        ...(lineItem.customAttributes && {
          customAttributes: lineItem.customAttributes.map(({ key, value }) => ({ key, value })),
        }),
        quantity: lineItem.quantity,
        variantId: lineItem.variant.id === prevVariantId ? variantId : lineItem.variant.id,
      }))
      const {
        data: { checkoutLineItemsReplace: data, userErrors: errors },
      } = await lineItemsReplace({
        variables: {
          checkoutId,
          lineItems,
        },
      })

      if (errors?.length) setErrors(errors)
      if (data) setCheckout(data?.checkout)
      setLoading(false)
    },
    [lineItemsReplace, setErrors, setCheckout, setLoading, checkout, checkoutId]
  )

  const updateItem = useCallback(
    async (variantId, quantity, customAttributes) => {
      setLoading(true)
      const lineItems = checkout?.lineItems?.map(lineItem =>
        lineItem.variant.id === variantId
          ? {
              customAttributes: [
                ...new Map(
                  [
                    ...lineItem?.customAttributes?.map(({ key, value }) => ({
                      key,
                      value,
                    })),
                    ...Object.entries(customAttributes)?.map(attr => ({
                      key: attr[0],
                      value: attr[1],
                    })),
                  ].map(item => [item?.key, item])
                ).values(),
              ],
              variantId,
              quantity,
            }
          : {
              ...(lineItem?.customAttributes && {
                customAttributes: lineItem.customAttributes.map(({ key, value }) => ({
                  key,
                  value,
                })),
              }),
              quantity: lineItem.quantity,
              variantId: lineItem.variant.id,
            }
      )

      const {
        data: { checkoutLineItemsReplace: data, userErrors: errors },
      } = await lineItemsReplace({
        variables: {
          checkoutId,
          lineItems,
        },
      })

      if (errors?.length) setErrors(errors)
      if (data) setCheckout(data?.checkout)
      setLoading(false)
    },
    [lineItemsReplace, setErrors, setCheckout, setLoading, checkout, checkoutId]
  )

  const clearCart = useCallback(async () => {
    setLoading(true)
    const isBrowser = typeof window !== `undefined`
    if (isBrowser) {
      window.ttq.track("ClickButton", {
        content_id: checkoutId,
        content_name: "Clear Cart",
      })
    }
    checkout?.lineItems?.map(({ variant, quantity }) => trackCartUpdate("remove", variant?.id, quantity, checkout?.lineItems))

    const {
      data: { checkoutLineItemsReplace: data, userErrors: errors },
    } = await lineItemsReplace({
      variables: {
        checkoutId,
        lineItems: [],
      },
    })

    if (errors?.length) setErrors(errors)
    if (data) setCheckout(data?.checkout)

    setLoading(false)
  }, [lineItemsReplace, setErrors, setCheckout, setLoading, trackCartUpdate, checkout, checkoutId])

  return {
    addToCart,
    multiAddToCart,
    removeFromCart,
    updateQuantity,
    updateVariant,
    updateItem,
    clearCart,
    loading,
    errors,
  }
}
